import api from './api';
import { plainText } from './journal';

// Voice call with Gladys, in the browser: record one utterance (stops after
// a silence), transcribe it on the server (Whisper), send it through the
// regular chat stream with voice: true, speak the reply sentence by
// sentence with the browser's speech synthesis as it streams in. Half
// duplex: never listening while she speaks, so she does not hear herself.

export const voiceSupport = () => Boolean(
  typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia
  && typeof window.MediaRecorder !== 'undefined'
  && window.speechSynthesis && typeof window.SpeechSynthesisUtterance !== 'undefined',
);

export const RECORDER_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];

export const pickMimeType = (Recorder = window.MediaRecorder) => RECORDER_TYPES.find((t) => Recorder?.isTypeSupported?.(t)) || '';

const extensionOf = (type) => (/wav/.test(type) ? 'wav' : /mp4|m4a|aac/.test(type) ? 'm4a' : /ogg/.test(type) ? 'ogg' : 'webm');

// Scaleway's Whisper only takes wav, mp3, flac and ogg, while browsers
// record webm (Chrome, Edge) or mp4 (Safari): the recording is decoded by the
// browser and re-encoded as 16 kHz mono 16-bit WAV, what Whisper works with
// anyway (~32 kB per second, 1.5 MB for the 45 s maximum).
export const WAV_RATE = 16000;

export const encodeWav = (samples, sampleRate = WAV_RATE) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const text = (offset, s) => { for (let i = 0; i < s.length; i += 1) view.setUint8(offset + i, s.charCodeAt(i)); };
  text(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // bytes per second
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  text(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
};

const readBlob = (blob) => (blob.arrayBuffer ? blob.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(blob);
}));

// toWav returns the recording as WAV, or the original blob when the browser
// cannot decode it (the server then reports the format it got).
export const toWav = async (blob, {
  AudioCtx = window.AudioContext || window.webkitAudioContext,
  OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext,
} = {}) => {
  if (!AudioCtx || !OfflineCtx || blob.type === 'audio/wav') return blob;
  const ctx = new AudioCtx();
  try {
    const decoded = await ctx.decodeAudioData(await readBlob(blob));
    const offline = new OfflineCtx(1, Math.max(1, Math.ceil(decoded.duration * WAV_RATE)), WAV_RATE);
    const source = offline.createBufferSource();
    source.buffer = decoded; // stereo is mixed down by the mono destination
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return encodeWav(rendered.getChannelData(0), WAV_RATE);
  } catch {
    return blob;
  } finally {
    ctx.close?.();
  }
};

// The shared client declares JSON by default, and axios turns a FormData
// sent as JSON into a JSON object (the audio Blob becomes {}): the upload
// must declare multipart, the browser then adds the boundary itself.
export const transcribe = async (blob) => {
  const audio = await toWav(blob);
  const form = new FormData();
  form.append('audio', audio, `utterance.${extensionOf(audio.type || '')}`);
  const res = await api.post('/voice/transcribe', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data?.text || '';
};

// Text as it should be said: markdown, links, emojis and table rows out.
export const speakable = (text) => plainText(
  String(text || '')
    .split('\n')
    .filter((l) => !l.trim().startsWith('|'))
    .map((l) => l.replace(/^\s*(#{1,6}|[-*+]|\d+[.)])\s+/, ''))
    .join('\n'),
)
  .replace(/https?:\/\/\S+/g, '')
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
  .replace(/\s+/g, ' ')
  .trim();

// takeSentences splits complete sentences off a streamed buffer; the rest
// waits for more text (or final = true at the end of the reply). A dot in
// a number ("3.5") or after one capital ("M. Dupont") does not end a sentence.
export const takeSentences = (buffer, { final = false } = {}) => {
  const sentences = [];
  let start = 0;
  const re = /([.!?…])(?=\s)|\n+/g;
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(buffer))) {
    const end = m.index + m[0].length;
    const piece = buffer.slice(start, end);
    if (m[1] === '.' && /(^|\s)[A-Z]\.$/.test(piece.trim())) continue;
    if (speakable(piece).length < 2) continue;
    sentences.push(piece.trim());
    start = end;
  }
  let rest = buffer.slice(start);
  if (final && speakable(rest)) {
    sentences.push(rest.trim());
    rest = '';
  }
  return { sentences, rest };
};

const VOICE_HINTS = ['google', 'amélie', 'amelie', 'audrey', 'aurélie', 'marie', 'denise', 'natural', 'premium', 'enhanced', 'thomas'];

// The best French voice the browser offers (null: the default voice).
export const pickVoice = (voices) => {
  const french = (voices || []).filter((v) => /^fr(-|_|$)/i.test(v.lang || ''));
  if (!french.length) return null;
  const score = (v) => {
    const name = (v.name || '').toLowerCase();
    let s = /fr[-_]fr/i.test(v.lang) ? 10 : 0;
    VOICE_HINTS.forEach((hint, i) => { if (name.includes(hint)) s += 20 - i; });
    if (v.localService === false) s += 3;
    return s;
  };
  return french.slice().sort((a, b) => score(b) - score(a))[0];
};

// Why Gladys cannot speak, shown on the call screen (she still answers in
// writing).
export const SPEECH_ISSUES = {
  'no-voices': 'Ce navigateur n’a aucune voix de synthèse : Gladys répond par écrit. Sur Linux, Firefox installé en snap et Chromium n’accèdent souvent pas aux voix du système ; Chrome, Edge, Safari ou un téléphone en ont.',
  blocked: 'Le navigateur a bloqué la voix de Gladys.',
  failed: 'La synthèse vocale a échoué dans ce navigateur : Gladys répond par écrit.',
};

// Errors that only mean we stopped the speech ourselves.
const IGNORED_SPEECH_ERRORS = new Set(['interrupted', 'canceled']);

// Audio element shared by the server voice: Safari only plays audio on an
// element that was first played from a user gesture, so primeSpeech unlocks
// this one and ServerSpeaker reuses it.
let sharedAudioElement = null;
export const sharedAudio = () => {
  if (!sharedAudioElement && typeof Audio !== 'undefined') sharedAudioElement = new Audio();
  return sharedAudioElement;
};

let silentUrl = null;
const silentAudioUrl = () => {
  if (!silentUrl) silentUrl = URL.createObjectURL(encodeWav(new Float32Array(160), WAV_RATE));
  return silentUrl;
};

// primeSpeech must run inside a click or tap handler: browsers that require
// a user gesture for speech (Safari on iOS, sometimes Chrome) unlock it for
// the page once speak() has been called from one, and the same goes for
// the audio element of the server voice. Says nothing (volume 0, muted).
export const primeSpeech = (synth = window.speechSynthesis, Utterance = window.SpeechSynthesisUtterance, audio = sharedAudio()) => {
  if (synth && Utterance) {
    try {
      synth.resume?.();
      const u = new Utterance(' ');
      u.volume = 0;
      synth.speak(u);
    } catch {
      // nothing to unlock
    }
  }
  if (audio && !audio.dataset?.unlocked) {
    try {
      audio.muted = true;
      audio.src = silentAudioUrl();
      const played = audio.play();
      const done = () => { audio.muted = false; if (audio.dataset) audio.dataset.unlocked = '1'; };
      if (played?.then) played.then(done, () => { audio.muted = false; });
      else done();
    } catch {
      audio.muted = false;
    }
  }
};

// loadVoices resolves with the browser's voices: Chrome and Firefox fill the
// list asynchronously (voiceschanged), so it waits up to waitMs for them.
export const loadVoices = (synth = window.speechSynthesis, waitMs = 1500) => new Promise((resolve) => {
  const now = synth?.getVoices?.() || [];
  if (now.length || !synth) {
    resolve(now);
    return;
  }
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    synth.removeEventListener?.('voiceschanged', finish);
    resolve(synth.getVoices?.() || []);
  };
  synth.addEventListener?.('voiceschanged', finish);
  setTimeout(finish, waitMs);
});

// Speaks streamed text sentence by sentence; onEnd fires once the reply is
// finished and every sentence has been said, onError(kind, code) when the
// browser refuses to speak (kind: blocked | failed).
export class Speaker {
  constructor({ synth = window.speechSynthesis, Utterance = window.SpeechSynthesisUtterance, voice = null, rate = 1.04, onStart, onEnd, onError } = {}) {
    Object.assign(this, { synth, Utterance, voice, rate, onStart, onEnd, onError });
    this.buffer = '';
    this.pending = 0;
    this.finished = false;
    this.cancelled = false;
    this.started = false;
    // Chrome may garbage-collect an utterance being spoken (no end event):
    // keep a reference until it is done.
    this.live = new Set();
  }

  say(sentence) {
    const text = speakable(sentence);
    if (!text || this.cancelled) return;
    const u = new this.Utterance(text);
    u.lang = 'fr-FR';
    u.rate = this.rate;
    if (this.voice) u.voice = this.voice;
    this.pending += 1;
    const done = () => {
      this.live.delete(u);
      this.pending -= 1;
      this.check();
    };
    u.onend = done;
    u.onerror = (event) => {
      const code = event?.error || 'unknown';
      if (!IGNORED_SPEECH_ERRORS.has(code) && !this.cancelled) this.onError?.(code === 'not-allowed' ? 'blocked' : 'failed', code);
      done();
    };
    this.live.add(u);
    if (!this.started) {
      this.started = true;
      this.onStart?.();
    }
    if (this.synth.paused) this.synth.resume?.();
    this.synth.speak(u);
  }

  push(delta) {
    if (this.cancelled) return;
    this.buffer += delta;
    const { sentences, rest } = takeSentences(this.buffer);
    this.buffer = rest;
    sentences.forEach((s) => this.say(s));
  }

  finish() {
    if (this.cancelled) return;
    const { sentences } = takeSentences(this.buffer, { final: true });
    this.buffer = '';
    sentences.forEach((s) => this.say(s));
    this.finished = true;
    this.check();
  }

  check() {
    if (this.finished && this.pending <= 0 && !this.cancelled) {
      this.finished = false;
      this.onEnd?.();
    }
  }

  cancel() {
    this.cancelled = true;
    this.buffer = '';
    this.live.clear();
    this.synth.cancel();
  }
}

// The server voice (POST /voice/speak): MP3 of a few sentences, read by
// OpenAI on the backend; the call prefers it to the browser's voice.
export const fetchSpeech = async (text, { signal } = {}) => {
  const res = await api.post('/voice/speak', { text }, { responseType: 'blob', signal });
  return res.data;
};

// The server reads at most this many characters per request (SpeechMaxChars).
const SPEECH_MAX_CHARS = 600;

// Same interface as Speaker, with the server voice: the first sentence is
// sent alone (Gladys starts sooner), the next ones grouped up to chunkChars
// (fewer cuts, so the intonation carries over); requests run ahead while the
// previous chunk plays, in order.
export class ServerSpeaker {
  constructor({ fetchAudio = fetchSpeech, audio = sharedAudio(), onStart, onEnd, onError, chunkChars = 360 } = {}) {
    Object.assign(this, { fetchAudio, audio, onStart, onEnd, onError, chunkChars });
    this.buffer = '';
    this.pendingText = '';
    this.queue = [];
    this.chunks = 0;
    this.playing = false;
    this.finished = false;
    this.cancelled = false;
    this.started = false;
    this.urls = new Set();
    this.controllers = new Set();
  }

  push(delta) {
    if (this.cancelled) return;
    this.buffer += delta;
    const { sentences, rest } = takeSentences(this.buffer);
    this.buffer = rest;
    sentences.forEach((s) => this.add(s));
  }

  add(sentence) {
    const text = speakable(sentence);
    if (!text) return;
    if (this.pendingText && this.pendingText.length + text.length >= SPEECH_MAX_CHARS) this.flushChunk();
    this.pendingText = this.pendingText ? `${this.pendingText} ${text}` : text;
    if (this.chunks === 0 || this.pendingText.length >= this.chunkChars) this.flushChunk();
  }

  flushChunk() {
    if (!this.pendingText || this.cancelled) return;
    const text = this.pendingText;
    this.pendingText = '';
    this.chunks += 1;
    const controller = new AbortController();
    this.controllers.add(controller);
    const audio = Promise.resolve()
      .then(() => this.fetchAudio(text, { signal: controller.signal }))
      .finally(() => this.controllers.delete(controller));
    audio.catch(() => {});
    this.queue.push(audio);
    this.pump();
  }

  finish() {
    if (this.cancelled) return;
    const { sentences } = takeSentences(this.buffer, { final: true });
    this.buffer = '';
    sentences.forEach((s) => this.add(s));
    this.flushChunk();
    this.finished = true;
    this.check();
  }

  pump() {
    if (this.playing || this.cancelled) return;
    const next = this.queue.shift();
    if (!next) {
      this.check();
      return;
    }
    this.playing = true;
    next.then((blob) => this.play(blob), (err) => {
      if (this.cancelled || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      this.onError?.('failed', err?.response?.status ? `HTTP ${err.response.status}` : 'network');
      this.playing = false;
      this.pump();
    });
  }

  play(blob) {
    if (this.cancelled) return;
    if (!this.started) {
      this.started = true;
      this.onStart?.();
    }
    const url = URL.createObjectURL(blob);
    this.urls.add(url);
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      this.urls.delete(url);
      this.playing = false;
      this.pump();
    };
    const fail = (kind, code) => {
      if (!settled && !this.cancelled) this.onError?.(kind, code);
      done();
    };
    this.audio.onended = done;
    this.audio.onerror = () => fail('failed', 'audio');
    this.audio.muted = false;
    this.audio.src = url;
    const played = this.audio.play();
    if (played?.catch) played.catch((err) => fail(err?.name === 'NotAllowedError' ? 'blocked' : 'failed', err?.name || 'play'));
  }

  check() {
    if (this.finished && !this.playing && this.queue.length === 0 && !this.pendingText && !this.cancelled) {
      this.finished = false;
      this.onEnd?.();
    }
  }

  cancel() {
    this.cancelled = true;
    this.buffer = '';
    this.pendingText = '';
    this.queue = [];
    this.controllers.forEach((c) => c.abort());
    this.controllers.clear();
    this.audio?.pause?.();
    this.urls.forEach((u) => URL.revokeObjectURL(u));
    this.urls.clear();
  }
}

// Decides when an utterance is over from the microphone level: speech
// starts above the threshold, the utterance ends after silenceMs of quiet
// once speech was heard; nothing heard after noSpeechMs, or maxMs reached.
export const createSilenceTracker = ({ threshold = 0.035, silenceMs = 1300, noSpeechMs = 9000, maxMs = 45000, minSpeechMs = 250 } = {}) => {
  let startedAt = null;
  let speechSince = null;
  let heard = false;
  let quietSince = null;
  return (level, now) => {
    if (startedAt === null) startedAt = now;
    if (now - startedAt >= maxMs) return heard ? 'done' : 'nothing';
    if (level >= threshold) {
      if (speechSince === null) speechSince = now;
      if (now - speechSince >= minSpeechMs) heard = true;
      quietSince = null;
      return 'continue';
    }
    speechSince = null;
    if (!heard) return now - startedAt >= noSpeechMs ? 'nothing' : 'continue';
    if (quietSince === null) quietSince = now;
    return now - quietSince >= silenceMs ? 'done' : 'continue';
  };
};

// Records one utterance from an open microphone stream. Resolves with the
// audio Blob, or null when nothing was said or it was aborted. stop() ends
// it at once (the "j'ai fini" button); onLevel(0..1) feeds the level meter.
export const recordUtterance = (stream, { onLevel, tracker = createSilenceTracker(), Recorder = window.MediaRecorder } = {}) => {
  const mimeType = pickMimeType(Recorder);
  const recorder = new Recorder(stream, mimeType ? { mimeType } : undefined);
  const chunks = [];
  let verdict = 'continue';
  let timer = null;
  let audioCtx = null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  let analyser = null;
  if (AudioCtx) {
    audioCtx = new AudioCtx();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    audioCtx.createMediaStreamSource(stream).connect(analyser);
  }
  const samples = analyser ? new Float32Array(analyser.fftSize) : null;
  const cleanup = () => {
    clearInterval(timer);
    audioCtx?.close?.();
  };
  const promise = new Promise((resolve) => {
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    recorder.onstop = () => {
      cleanup();
      resolve(verdict === 'done' && chunks.length ? new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }) : null);
    };
  });
  recorder.start(250);
  if (analyser) {
    timer = setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
      const level = Math.sqrt(sum / samples.length);
      onLevel?.(Math.min(1, level * 6));
      const next = tracker(level, performance.now());
      if (next !== 'continue' && recorder.state === 'recording') {
        verdict = next;
        recorder.stop();
      }
    }, 60);
  }
  return {
    promise,
    stop: () => { if (recorder.state === 'recording') { verdict = 'done'; recorder.stop(); } },
    abort: () => { if (recorder.state === 'recording') { verdict = 'aborted'; recorder.stop(); } },
  };
};
